import { useEffect, useState } from "react";
import { Checkbox, DatePicker, Select } from "antd";
import { Check, Undo2, UserCheck, X } from "lucide-react";
import dayjs from "dayjs";
import {
    EMPLOYEE_PERSONAL_INFO,
    TRANSFER_REPORTING_MANAGER,
    TRANSFER_REPORTING_MANAGER_INBOX,
} from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { Reactable } from "../../components/ui/reactable";
import { cn } from "../../utils/tailwindUtils";

const { Option } = Select;

const RIGHTS = [
    { key: "leave", label: "Leave" },
    { key: "reimbursement", label: "Reimbursement" },
    { key: "advance_salary", label: "Advance salary" },
    { key: "resignation", label: "Resignation" },
];

const emptyRights = () =>
    RIGHTS.reduce((acc, item) => ({ ...acc, [item.key]: false }), {});

const parseList = (res) => {
    const raw = res?.data?.result || res?.data || [];
    return Array.isArray(raw) ? raw : [];
};

const employeeLabel = (row) => {
    const details = row?.employee_details || row?.sent_from_employee || {};
    const name =
        details.employee_name ||
        row?.employee ||
        row?.sent_from ||
        details.full_name ||
        `${details.first_name || ""} ${details.last_name || ""}`.trim();
    const code = details.employee_id || details.employee_code;
    return code ? `${name} (${code})` : name || "-";
};

const rightsSummary = (perms) => {
    let rights = perms;
    if (typeof rights === "string") {
        try {
            rights = JSON.parse(rights);
        } catch {
            return "-";
        }
    }
    if (!rights || typeof rights !== "object") return "-";
    const selected = RIGHTS.filter((item) => rights[item.key]).map((item) => item.label);
    return selected.length ? selected.join(", ") : "None";
};

const TransferReportingManager = () => {
    const { fetch, create, update } = useApiMethods();
    const [tab, setTab] = useState("requests");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [confirm, setConfirm] = useState({ open: false, row: null, action: "" });
    const [form, setForm] = useState({
        employee: undefined,
        from_date: null,
        to_date: null,
        todate_optional: false,
        comments: "",
        rights: emptyRights(),
    });

    const myEmployeeId = Number(sessionStorage.getItem("employee_id") || 0);

    useEffect(() => {
        setLoading(true);
        const url = tab === "inbox" ? TRANSFER_REPORTING_MANAGER_INBOX : TRANSFER_REPORTING_MANAGER;
        fetch(url)
            .then((res) => setRows(parseList(res)))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [tab, trigger]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!openForm) return;
        fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True", is_active: "True" })
            .then((res) => setEmployees(parseList(res)))
            .catch(() => setEmployees([]));
    }, [openForm]); // eslint-disable-line react-hooks/exhaustive-deps

    const closeForm = () => {
        setForm({
            employee: undefined,
            from_date: null,
            to_date: null,
            todate_optional: false,
            comments: "",
            rights: emptyRights(),
        });
        setOpenForm(false);
    };

    const submit = async () => {
        if (!form.employee) {
            ToastMsg("error", "Select the temporary reporting manager");
            return;
        }
        if (!form.from_date) {
            ToastMsg("error", "From Date is required");
            return;
        }
        if (!form.todate_optional && !form.to_date) {
            ToastMsg("error", "To Date is required unless the end date is open");
            return;
        }
        if (form.to_date && form.from_date && dayjs(form.to_date).isBefore(form.from_date, "day")) {
            ToastMsg("error", "To Date cannot be before From Date");
            return;
        }
        if (!RIGHTS.some((item) => form.rights[item.key])) {
            ToastMsg("error", "Select at least one approval right");
            return;
        }
        try {
            setLoading(true);
            const payload = {
                employee: form.employee,
                from_date: dayjs(form.from_date).format("YYYY-MM-DD"),
                to_date: form.to_date ? dayjs(form.to_date).format("YYYY-MM-DD") : null,
                todate_optional: !!form.todate_optional,
                comments: form.comments || "",
                is_employee_transfer: Number(form.employee) !== myEmployeeId,
                approval_inbox_permissions: JSON.stringify({
                    leave: !!form.rights.leave,
                    reimbursement: !!form.rights.reimbursement,
                    advance_salary: !!form.rights.advance_salary,
                    resignation: !!form.rights.resignation,
                }),
            };
            const res = await create(TRANSFER_REPORTING_MANAGER, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Transfer reporting manager request submitted");
                closeForm();
                setTrigger((prev) => !prev);
            } else {
                ToastMsg("error", res?.message || "Failed to save request");
            }
        } catch (error) {
            /* interceptor toasts */
        } finally {
            setLoading(false);
        }
    };

    const actInbox = async (row, status) => {
        try {
            setLoading(true);
            const res = await create(TRANSFER_REPORTING_MANAGER_INBOX, {
                transfer_id: row.id,
                status,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || `Request ${status}`);
                setConfirm({ open: false, row: null, action: "" });
                setTrigger((prev) => !prev);
            } else {
                ToastMsg("error", res?.message || "Action failed");
            }
        } catch (error) {
            /* interceptor toasts */
        } finally {
            setLoading(false);
        }
    };

    const revert = async (row) => {
        try {
            setLoading(true);
            const res = await update(TRANSFER_REPORTING_MANAGER, {
                id: row.id,
                employee: row.employee_details?.id || row.employee_id || row.employee,
                revert_status: true,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Delegation reverted");
                setConfirm({ open: false, row: null, action: "" });
                setTrigger((prev) => !prev);
            } else {
                ToastMsg("error", res?.message || "Could not revert");
            }
        } catch (error) {
            /* interceptor toasts */
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: tab === "inbox" ? "From" : "Temporary RM",
            dataIndex: "employee",
            key: "employee",
            render: (_value, row) => employeeLabel(row),
        },
        {
            title: "From Date",
            dataIndex: "from_date",
            key: "from_date",
            render: (value) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
        },
        {
            title: "To Date",
            dataIndex: "to_date",
            key: "to_date",
            render: (value, row) =>
                value ? dayjs(value).format("DD-MM-YYYY") : row.todate_optional ? "Open" : "-",
        },
        {
            title: "Rights",
            dataIndex: "approval_inbox_permissions",
            key: "rights",
            render: (value) => rightsSummary(value),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value, row) =>
                row.revert_status ? "reverted" : value || "-",
        },
        {
            title: "Action",
            key: "action",
            render: (_value, row) => {
                if (tab === "inbox" && row.status === "pending") {
                    return (
                        <div className="flex gap-2">
                            <Button
                                size="icon"
                                variant="ghost"
                                title="Approve"
                                onClick={() => setConfirm({ open: true, row, action: "approved" })}
                            >
                                <Check className="h-4 w-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                title="Reject"
                                onClick={() => setConfirm({ open: true, row, action: "rejected" })}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    );
                }
                if (
                    tab === "requests" &&
                    row.status === "approved" &&
                    !row.revert_status
                ) {
                    return (
                        <Button
                            size="icon"
                            variant="ghost"
                            title="Revert now"
                            onClick={() => setConfirm({ open: true, row, action: "revert" })}
                        >
                            <Undo2 className="h-4 w-4" />
                        </Button>
                    );
                }
                return <span className="text-xs text-gray-400">—</span>;
            },
        },
    ];

    const label = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";
    const field = "w-full";

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={UserCheck}
                title="Transfer Reporting Manager"
                subtitle="Assign a temporary reporting manager for a date range and selected approval rights."
                buttonEnable={tab === "requests"}
                buttonName="New Request"
                setOpenDialog={() => setOpenForm(true)}
            />

            <div className="flex gap-2 mb-4">
                {[["requests", "My Requests"], ["inbox", "Approval Inbox"]].map(([key, lbl]) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={cn(
                            "px-4 py-1.5 rounded-lg text-sm font-medium transition-colors",
                            tab === key
                                ? "bg-sidebar-primary text-white"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                        )}
                    >
                        {lbl}
                    </button>
                ))}
            </div>

            <Reactable
                key={tab}
                data={rows}
                setData={setRows}
                columns={columns}
                isCSVRequired
                isPDFRequired
            />
            {loading && (
                <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>
            )}

            <DialogModal
                open={openForm}
                onClose={closeForm}
                onSave={submit}
                title="Assign Temporary Reporting Manager"
                saveLabel="Submit"
                loading={loading}
                width="560px"
            >
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className={label}>Temporary reporting manager <span className="text-red-500">*</span></label>
                        <Select
                            showSearch
                            optionFilterProp="children"
                            className={field}
                            size="large"
                            placeholder="Select employee"
                            value={form.employee}
                            onChange={(value) => setForm((prev) => ({ ...prev, employee: value }))}
                        >
                            {employees.map((emp) => (
                                <Option key={emp.id} value={emp.id}>
                                    {`${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.employee_id} ({emp.employee_id})
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label className={label}>From Date <span className="text-red-500">*</span></label>
                        <DatePicker
                            className={field}
                            size="large"
                            value={form.from_date}
                            onChange={(value) => setForm((prev) => ({ ...prev, from_date: value }))}
                        />
                    </div>
                    <div>
                        <label className={label}>To Date {!form.todate_optional && <span className="text-red-500">*</span>}</label>
                        <DatePicker
                            className={field}
                            size="large"
                            disabled={form.todate_optional}
                            value={form.to_date}
                            onChange={(value) => setForm((prev) => ({ ...prev, to_date: value }))}
                        />
                    </div>
                    <div className="col-span-2">
                        <Checkbox
                            checked={form.todate_optional}
                            onChange={(event) =>
                                setForm((prev) => ({
                                    ...prev,
                                    todate_optional: event.target.checked,
                                    to_date: event.target.checked ? null : prev.to_date,
                                }))
                            }
                        >
                            No end date — revert manually when the assignment should stop
                        </Checkbox>
                    </div>
                    <div className="col-span-2">
                        <label className={label}>Approval rights</label>
                        <p className="text-xs text-gray-400 mb-2">
                            Only checked request types go to the temporary manager. Unchecked types stay with you.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {RIGHTS.map((item) => (
                                <Checkbox
                                    key={item.key}
                                    checked={!!form.rights[item.key]}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            rights: { ...prev.rights, [item.key]: event.target.checked },
                                        }))
                                    }
                                >
                                    {item.label}
                                </Checkbox>
                            ))}
                        </div>
                    </div>
                    <div className="col-span-2">
                        <label className={label}>Comments</label>
                        <textarea
                            className="w-full min-h-[80px] rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                            value={form.comments}
                            onChange={(event) => setForm((prev) => ({ ...prev, comments: event.target.value }))}
                        />
                    </div>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={confirm.open && confirm.action === "approved"}
                onClose={() => setConfirm({ open: false, row: null, action: "" })}
                onConfirm={() => actInbox(confirm.row, "approved")}
                title="Approve transfer"
                description="The selected employee will receive the checked approval rights for the date range."
                confirmLabel="Approve"
                loading={loading}
            />
            <ConfirmPopupModal
                open={confirm.open && confirm.action === "rejected"}
                onClose={() => setConfirm({ open: false, row: null, action: "" })}
                onConfirm={() => actInbox(confirm.row, "rejected")}
                title="Reject transfer"
                description="This temporary reporting manager request will be rejected."
                confirmLabel="Reject"
                loading={loading}
            />
            <ConfirmPopupModal
                open={confirm.open && confirm.action === "revert"}
                onClose={() => setConfirm({ open: false, row: null, action: "" })}
                onConfirm={() => revert(confirm.row)}
                title="Revert now"
                description="Approvals will return to the original reporting manager immediately."
                confirmLabel="Revert"
                loading={loading}
            />
        </ContainerCard>
    );
};

export default TransferReportingManager;
